import { isWeb } from '@lib/constants'
import { Colors } from '@vetahealth/fishing-gear/colors'
import noop from 'lodash/noop'
import React, { useState } from 'react'
import { PixelRatio, Platform, TextInput, TextInputProps } from 'react-native'
import styled from 'styled-components/native'
import { MouseOverType } from './types'

const Input = styled(TextInput).attrs(({ theme }) => ({
  selectionColor: theme.colors.primary,
  placeholderTextColor: Colors.gray500,
}))<MouseOverType & { isFocused: boolean; numberOfLines: number }>`
  font-family: ${({ theme }) => theme.font.weight.semiBold.fontFamily};
  background-color: ${({ theme, editable }) => (editable ? theme.colors.secondaryBackground : theme.colors.background)};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.font.size.medium / PixelRatio.getFontScale()}px;
  min-height: ${({ theme, numberOfLines = 1 }) =>
    numberOfLines > 1 ? numberOfLines * ((theme.font.size.medium / PixelRatio.getFontScale()) * 1.4) + 44 : 44}px;
  padding: 0 20px;
  padding-top: ${({ numberOfLines = 1 }) => (numberOfLines !== 1 ? 15 : Platform.select({ ios: 0, android: 3, web: 0 }))}px;
  padding-bottom: ${({ numberOfLines = 1 }) => (numberOfLines !== 1 ? 15 : Platform.select({ ios: 0, android: 3, web: 0 }))}px;
  border-radius: ${({ theme }) => theme.borderRadius.medium}px;
  outline-width: 2px;
  outline-color: ${({ isFocused, isHovered, theme }) =>
    isFocused ? theme.colors.primary : isHovered ? Colors.mauve500 : theme.colors.border};
  ${({ theme }) =>
    isWeb &&
    `
    font-weight: ${theme.font.weight.semiBold.fontWeight};
    field-sizing: content;
  `}
  ${({ theme, numberOfLines }) =>
    numberOfLines !== 1 &&
    `
    line-height: ${(theme.font.size.medium / PixelRatio.getFontScale()) * 1.4}px;
  `}
  ${({ editable, theme }) =>
    !editable &&
    `
    outline-style: solid;
    outline-color: ${theme.colors.border};
    color: ${theme.colors.secondaryText};
  `}
`

export const InputWrapper = styled.View``
export function BaseInput({
  'aria-label': ariaLabel,
  autoFocus = false,
  inputMode,
  maxLength,
  numberOfLines = 1,
  onChange = noop,
  onChangeText = noop,
  onKeyPress,
  onFocus = noop,
  onSubmitEditing = noop,
  placeholder,
  enterKeyHint,
  secureTextEntry,
  textAlignVertical,
  textContentType,
  value = '',
  style,
  selectTextOnFocus,
  selection,
  editable = true,
  ref,
}: TextInputProps & { ref?: React.Ref<TextInput> }) {
  const [isFocused, setIsFocused] = useState<boolean>(autoFocus)
  const [isHovered, setIsHovered] = useState<boolean>(false)

  return (
    <Input
      aria-label={ariaLabel}
      aria-disabled={!editable}
      autoFocus={autoFocus}
      editable={editable}
      isFocused={isFocused}
      isHovered={isHovered}
      inputMode={inputMode}
      maxLength={maxLength}
      multiline={numberOfLines !== 1}
      numberOfLines={numberOfLines}
      onBlur={() => {
        setIsFocused(false)
        setIsHovered(false)
      }}
      onChange={onChange}
      onChangeText={onChangeText}
      onFocus={(e) => {
        setIsFocused(true)
        onFocus?.(e)
      }}
      onKeyPress={onKeyPress}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onSubmitEditing={onSubmitEditing}
      placeholder={placeholder}
      ref={ref}
      enterKeyHint={enterKeyHint}
      secureTextEntry={secureTextEntry}
      textAlignVertical={textAlignVertical}
      textContentType={textContentType}
      value={value}
      style={style}
      selectTextOnFocus={selectTextOnFocus}
      selection={selection}
    />
  )
}
